import React, {
  useEffect,
  useState,
  useLayoutEffect,
  MutableRefObject,
  useContext,
  useRef,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CodeFile } from "utils/interfaces";
import { LoadingDots } from "utils/loadingAnimation";
import {
  EditorContext,
  ScratchEnvironmentContext,
} from "components/utils/Contexts";
import { jsonSB3ToText } from "utils/scratchFunctions";
import { postScratchRequest } from "./utils/postScratchRequest";
import { getTimestamp } from "utils/utils";
import { MdClose } from "react-icons/md";
import processFileFromScratch from "./utils/processFileFromScratch";

interface ScratchEditorProps {
  sb3File?: ArrayBuffer | null;
  videoExpanded: boolean;
  iframeRef: MutableRefObject<HTMLIFrameElement | null>;
  onFileChange: (files: CodeFile[]) => void;
}

// Parameters
const MIN_SCRATCH_HEIGHT = 640;

const ScratchEditor: React.FC<ScratchEditorProps> = ({
  sb3File,
  videoExpanded,
  iframeRef,
  onFileChange,
}) => {
  // Define the source of Scratch
  const iframeSrc =
    process.env.REACT_APP_SCRATCH_URL || "http://localhost:8602";

  // States definition
  const [scaleFactor, setScaleFactor] = useState(1);
  const [scaledTopOffset, setScaledTopOffset] = useState(0);
  const [scaledRightOffset, setScaledRightOffset] = useState(0);
  const [showCodeSavedError, setShowCodeSavedError] = useState(false);

  // Define a reference for the iframe container div
  const iframeContainerRef = useRef<HTMLDivElement | null>(null);

  // Get the editor context parameters
  const {
    title,
    userFirstName,
    userLastName,
  }: { title: string; userFirstName: string; userLastName: string } =
    useContext(EditorContext);

  // Get the controller that defines the pointer behaviour of the Scratch Editor
  const {
    isDraggingResizingPanel,
    setHighlightedScratchElement,
    guiReady,
    setGuiReady,
    scaleResizeIframe,
    setScaleResizeIframe,
    handleSendSavedFileToS3,
    isUnsavedProject,
    setIsUnsavedProject,
    loadScratchCode,
  }: {
    isDraggingResizingPanel: boolean;
    setHighlightedScratchElement: React.Dispatch<
      React.SetStateAction<{
        top: number;
        left: number;
        width: number;
        height: number;
      }>
    >;
    guiReady: boolean;
    setGuiReady: React.Dispatch<React.SetStateAction<boolean>>;
    scaleResizeIframe: number;
    setScaleResizeIframe: React.Dispatch<React.SetStateAction<number>>;
    handleSendSavedFileToS3: (file: string | ArrayBuffer) => Promise<boolean>;
    isUnsavedProject: boolean;
    setIsUnsavedProject: React.Dispatch<React.SetStateAction<boolean>>;
    loadScratchCode: (sb3File: ArrayBuffer | null | undefined) => void;
  } = useContext(ScratchEnvironmentContext);

  /**
   * Timer for the saved error pop up message
   */
  useEffect(() => {
    if (showCodeSavedError) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        setShowCodeSavedError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showCodeSavedError]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      try {
        switch (event.data.type) {
          case "SCRATCH_GUI_READY":
            if (!guiReady) setGuiReady(true);
            break;
          case "SCRATCH_JSON": {
            const blocks = await jsonSB3ToText(JSON.parse(event.data.json));
            onFileChange([{ name: "main.sb3", content: blocks.text }]);
            break;
          }
          case "GET_SCRATCH_PROJECT": {
            // Transform blob into base64
            const reader = new FileReader();
            reader.readAsDataURL(event.data.json);
            reader.onloadend = () => {
              const base64File = reader.result;
            };
            break;
          }
          case "CHECK_SCRATCH_PROJECT": {
            // Transform blob into base64
            const reader = new FileReader();
            reader.readAsDataURL(event.data.json);
            reader.onloadend = () => {
              const base64File = reader.result as string;
            };
            break;
          }
          case "SAVE_SCRATCH_PROJECT": {
            // Transform blob into base64
            const base64File = await processFileFromScratch(event);

            // Check the file is defined
            if (!base64File) {
              console.log("Code save unsuccessful!\n(base64 not found)");
              return;
            }

            // Send the file to S3
            const s3CodeUpload = await handleSendSavedFileToS3(base64File);

            // Check if the code was saved successfully
            if (s3CodeUpload) {
              postScratchRequest(iframeRef, "SET_SAVE_STATUS", {
                saveStatus: "saved",
              });
              console.log("Code saved successfully!");
            } else {
              setShowCodeSavedError(true);
              console.log("Code save unsuccessful!");
            }
            break;
          }
          case "DOWNLOAD_SCRATCH_PROJECT": {
            // Transform blob into base64
            const base64File = await processFileFromScratch(event);

            // Check the file is defined
            if (!base64File) {
              console.log("Code save unsuccessful!");
              return;
            }

            // Get a date stamp for this file
            const date = new Date(Date.now());
            const timestamp = getTimestamp(date);

            // Finally, transform all of the spaces into underscores
            const titleFormatted = title.replaceAll(" ", "_");

            // Create a link element and trigger a download
            const a = document.createElement("a");
            a.href = base64File;
            a.download = `${userFirstName}_${userLastName}_${titleFormatted}_${timestamp}.sb3`;
            document.body.appendChild(a);
            a.click();

            // Clean up the URL
            const timerId = setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(base64File);
            }, 100);

            // Remove the timer
            clearTimeout(timerId);
            break;
          }
          case "PROJECT_LOADED":
            console.log("Project loaded successfully in GUI");
            break;
          case "PROJECT_LOAD_ERROR":
            console.error("Error loading project in GUI:", event.data.error);
            break;
          case "TOOLBAR_AND_BLOCKS":
            setHighlightedScratchElement(event.data.elementInfo);
            break;
          case "CODE_AREA":
            setHighlightedScratchElement(event.data.elementInfo);
            break;
          case "STAGE":
            setHighlightedScratchElement(event.data.elementInfo);
            break;
          case "SPRITE_GALLERY":
            setHighlightedScratchElement(event.data.elementInfo);
            break;
          case "SCRATCH_AUTOSAVE_TRIGGER":
            triggerCheckUnsavedProject();
            break;
        }
      } catch (error) {
        console.error(
          "Error when processing the message from the Scratch iframe:",
          error
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [guiReady]);

  /**
   * Hook used to load the project once the scratch UI is loaded
   */
  useEffect(() => {
    loadScratchCode(sb3File);
  }, [guiReady, sb3File]);

  /**
   * Function that triggers the check unsaved project
   * @returns
   */
  const triggerCheckUnsavedProject = () => {
    // Check if we are currently checking
    if (isUnsavedProject) return;

    // Block the process
    setIsUnsavedProject(true);
    // And send the message to change the save icon status in the Scratch GUI
    postScratchRequest(iframeRef, "SET_SAVE_STATUS", { saveStatus: "unsaved" });
  };

  // For resizing Scratch IFrame
  useLayoutEffect(() => {
    const handleResize = () => {
      const targetWidth = 152;
      const originalWidth = iframeRef?.current?.clientWidth || 0; // 0 x padding
      const widthScale = targetWidth / originalWidth;
      const targetHeight = 94;
      const originalHeight = iframeRef?.current?.clientHeight || 0 - 155; // 125 is top offset
      const heightScale = targetHeight / originalHeight;
      const rightOffset = (targetWidth - originalWidth * heightScale) / 2;
      const topOffset = (targetHeight - originalHeight * widthScale) / 2;

      if (widthScale < heightScale) {
        setScaleFactor(widthScale);
        setScaledTopOffset(topOffset);
        setScaledRightOffset(0);
      } else {
        setScaleFactor(heightScale);
        setScaledTopOffset(0);
        setScaledRightOffset(rightOffset);
      }

      // Get the height of the iframe to see how to scale it
      const windowHeight = window.innerHeight;

      // Update the scale of the iframe
      if (windowHeight - 111 <= MIN_SCRATCH_HEIGHT) {
        setScaleResizeIframe((windowHeight - 111) / MIN_SCRATCH_HEIGHT);
      } else setScaleResizeIframe(1);
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <motion.div
        className="w-screen h-[calc(100vh-112px)] overflow-hidden absolute origin-top-right"
        style={{
          zIndex: videoExpanded ? -10 : "auto",
        }}
        initial={{
          transform: "translateX(0%) scale(1)",
          top: 112,
          right: 0,
        }}
        animate={{
          transform: videoExpanded
            ? `scale(${scaleFactor * 0.98})`
            : "scale(1)",
          top: videoExpanded
            ? +11 + scaledTopOffset + (scaleFactor * 30) / 2
            : 112,
          right: videoExpanded ? 54 + scaledRightOffset : 0,
          transition: { ease: "linear" },
        }}
      >
        <div
          ref={iframeContainerRef}
          className={`relative w-full h-full ${
            isDraggingResizingPanel
              ? "pointer-events-none"
              : "pointer-events-auto"
          } flex flex-col rounded-xl`}
        >
          <AnimatePresence>
            {!guiReady && (
              <motion.div
                className="absolute w-full h-full flex justify-center items-center bg-white"
                initial={{
                  backdropFilter: "blur(4px)",
                  opacity: 1,
                }}
                animate={{
                  backdropFilter: guiReady ? "blur(0px)" : "blur(4px)",
                  opacity: guiReady ? 0 : 1,
                }}
                exit={{
                  backdropFilter: "blur(0px)",
                  opacity: 0,
                }}
              >
                <LoadingDots bgColor="bg-secondary-600" />
              </motion.div>
            )}
          </AnimatePresence>
          <iframe
            ref={iframeRef}
            id="scratch-gui"
            src={iframeSrc}
            className="w-full origin-top border-none px-10"
            style={{
              height: "100%",
              minHeight: MIN_SCRATCH_HEIGHT,
              transform: `scaleY(${scaleResizeIframe})`,
            }}
            allowFullScreen
            allow="microphone"
            title="Scratch Editor"
          />
        </div>
      </motion.div>
      <AnimatePresence>
        {showCodeSavedError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="fixed w-fit h-fit left-0 right-0 ml-auto mr-auto bottom-4 p-2 px-5 bg-error-500 rounded-3xl justify-center items-center gap-2 inline-flex z-30 select-none "
          >
            <MdClose className="w-5 h-5 fill-white" />
            <div className="text-white text-sm font-normal font-sans z-10">
              Error when saving file
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ScratchEditor;
